'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { useId, useState, type ReactNode } from 'react';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { PROFILE_COPY } from '@eliadmin/core/src/components/dlz/account/profile/profile-copy';
import { DialogCard } from '@eliadmin/core/src/components/dlz/dialogs/dialog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Switch } from '@eliadmin/core/src/components/ui/switch';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Toggle“ (60320:19075): 44 × 24 track – bg/brand on, border/subtle off, bg/disabled when
 * disabled (always shown on) – with a 20 px white knob 2 px from the edge, shadow 0 1 2 / 15 %.
 */
const SWITCH = {
  wrapper:
    'h-6 w-11 shadow-none focus-visible:ring-[#0F1012] focus-visible:ring-offset-white disabled:opacity-100 data-[state=checked]:bg-[#D7191F] data-[state=unchecked]:bg-[#E2E4E7] disabled:data-[state=checked]:bg-[#ECEDEF]',
  thumb: 'h-5 w-5 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.15)] data-[state=checked]:translate-x-5',
};

/**
 * Figma „Setting“ (60321:19250): 1 px border/subtle line above, 16 px padding, DLZ/Body/M Medium
 * title over a DLZ/Body/S description, the switch on the right.
 */
function SettingRow({
  title,
  description,
  descriptionTone = 'default',
  checked,
  disabled,
  busy,
  onCheckedChange,
}: {
  title: string;
  description: ReactNode;
  descriptionTone?: 'default' | 'error';
  checked: boolean;
  disabled?: boolean;
  busy?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <li className="flex items-center gap-4 border-t border-[#E2E4E7] py-4">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p id={titleId} className="dlz-body-m-medium text-[#0F1012]">
          {title}
        </p>
        <p
          id={descriptionId}
          aria-live="polite"
          className={cn(
            'dlz-body-s',
            descriptionTone === 'error' ? 'text-[#D7191F]' : 'text-[#6B7079]',
          )}
        >
          {description}
        </p>
      </div>
      <Switch
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        aria-busy={busy || undefined}
        checked={checked}
        disabled={disabled || busy}
        onCheckedChange={onCheckedChange}
        classNames={SWITCH}
      />
    </li>
  );
}

type NewsletterState =
  | { status: 'idle' | 'subscribing' | 'pending' | 'unsubscribed' }
  | { status: 'failed'; message: string };

/**
 * „Novinky a zľavy e-mailom“ bound to `newsletter_subscribed`. Switching on subscribes the account
 * e-mail like every newsletter form of the core (double opt-in: the subscription starts with the
 * confirmation link). Switching off follows the core profile: a confirmation, then the customer's
 * `newsletter_unsubscribe_url` in a new tab; without that link the switch stays on and says how to
 * leave.
 */
function NewsletterSetting({ customer, authToken }: { customer: Customer; authToken: string }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const [state, setState] = useState<NewsletterState>({ status: 'idle' });
  const [confirming, setConfirming] = useState(false);
  const subscribed = !!customer.newsletter_subscribed;
  const unsubscribeUrl = customer.newsletter_unsubscribe_url;
  const copy = PROFILE_COPY.newsletter;

  const subscribe = async () => {
    setState({ status: 'subscribing' });
    try {
      await newsletterSubscribe({
        locale,
        body: {
          email: customer.email,
          gdpr: true,
          return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
        },
      });
      setState({ status: 'pending' });
    } catch (error) {
      const messages = error instanceof ValidationError ? Object.values(error.fields).flat() : [];
      setState({ status: 'failed', message: messages.join(' ') || t('text573') });
    }
  };

  const unsubscribe = () => {
    initCustomerStore({ authToken, customer: { ...customer, newsletter_subscribed: false } });
    setState({ status: 'unsubscribed' });
    setConfirming(false);
  };

  const description =
    state.status === 'failed'
      ? state.message
      : state.status === 'pending'
        ? copy.pending(customer.email)
        : state.status === 'unsubscribed'
          ? t('text3225')
          : subscribed && !unsubscribeUrl
            ? copy.unsubscribeByEmail
            : copy.text;

  return (
    <>
      <SettingRow
        title={copy.title}
        description={description}
        descriptionTone={state.status === 'failed' ? 'error' : 'default'}
        checked={subscribed || state.status === 'pending'}
        disabled={state.status === 'pending' || (subscribed && !unsubscribeUrl)}
        busy={state.status === 'subscribing'}
        onCheckedChange={next => (next ? subscribe() : setConfirming(true))}
      />
      {unsubscribeUrl ? (
        <Dialog.Root open={confirming} onOpenChange={setConfirming}>
          <DialogCard title={t('text3226')} description={t('text3224')} className="max-w-[539px]">
            <div className="flex flex-col gap-3 md:flex-row">
              <Button asChild iconRight="arrow-up-right" className="h-12 md:h-[52px]">
                <a
                  href={unsubscribeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={unsubscribe}
                >
                  {t('text3223')}
                </a>
              </Button>
              <Dialog.Close asChild>
                <Button variant="secondary" className="h-12 md:h-[52px]">
                  {t('text272')}
                </Button>
              </Dialog.Close>
            </div>
          </DialogCard>
        </Dialog.Root>
      ) : null}
    </>
  );
}

/**
 * Figma „Komunikácia a súhlasy“ (60321:19247; mobile 60459:88693). Only the newsletter is backed by
 * customer data; the designed „SMS o stave objednávky“ and „Dotazník spokojnosti po nákupe“ rows have
 * no field in the API and are not shown. Without newsletter data the card is left out.
 */
export function CommunicationCard({
  customer,
  authToken,
}: {
  customer: Customer;
  authToken: string;
}) {
  if (typeof customer.newsletter_subscribed !== 'boolean') return null;

  return (
    <AccountCard title={PROFILE_COPY.communication}>
      <ul className="flex flex-col">
        <NewsletterSetting customer={customer} authToken={authToken} />
      </ul>
    </AccountCard>
  );
}
